import React, { useMemo } from 'react';
import styled from 'styled-components';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { backofficeApi, formatCurrency, formatDate } from '@kardume/services';
import { theme } from '@kardume/theme';
import type { InvoiceDto, InvoiceStatus } from '@kardume/types';
import { PageHeader, StatCard, Table, TableContainer, Badge } from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  PAID: 'Paga',
  OPEN: 'Em aberto',
  OVERDUE: 'Vencida',
  CANCELED: 'Cancelada',
};

/** Map an invoice status to a Badge tone. */
function statusTone(status: InvoiceStatus): 'success' | 'warning' | 'danger' {
  if (status === 'PAID') return 'success';
  if (status === 'OPEN') return 'warning';
  return 'danger';
}

/** Sum invoice amounts filtered by status. */
function sumByStatus(invoices: InvoiceDto[], status: InvoiceStatus): number {
  return invoices
    .filter((invoice) => invoice.status === status)
    .reduce((total, invoice) => total + invoice.amount, 0);
}

/** Backoffice financial overview: platform invoices and receivables. */
const BillingPage: React.FC = () => {
  const { data, loading, error, reload } = useAsync(
    () => backofficeApi.listInvoices({ size: 100 }),
    []
  );

  const invoices = useMemo<InvoiceDto[]>(() => data?.content ?? [], [data]);

  const totals = useMemo(
    () => ({
      paid: sumByStatus(invoices, 'PAID'),
      open: sumByStatus(invoices, 'OPEN'),
      overdue: sumByStatus(invoices, 'OVERDUE'),
    }),
    [invoices]
  );

  return (
    <>
      <PageHeader
        title="Financeiro"
        subtitle="Faturamento e recebíveis das assinaturas do Kardume Urbano."
      />

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={invoices.length === 0}
        emptyTitle="Nenhuma fatura encontrada"
        emptyDescription="Assim que as assinaturas gerarem cobranças, as faturas aparecerão aqui."
      >
        <Grid>
          <StatCard
            label="Total pago"
            value={formatCurrency(totals.paid)}
            icon={<CheckCircle2 size={18} />}
            iconColor={theme.success}
          />
          <StatCard
            label="Em aberto"
            value={formatCurrency(totals.open)}
            icon={<Clock size={18} />}
            iconColor={theme.warning}
          />
          <StatCard
            label="Vencido"
            value={formatCurrency(totals.overdue)}
            icon={<AlertTriangle size={18} />}
            iconColor={theme.error}
          />
        </Grid>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Lojista</th>
                <th>Plano</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Vencimento</th>
                <th>Pagamento</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <strong>{invoice.merchantName}</strong>
                  </td>
                  <td>{invoice.planName}</td>
                  <td>{formatCurrency(invoice.amount)}</td>
                  <td>
                    <Badge tone={statusTone(invoice.status)}>
                      {STATUS_LABELS[invoice.status]}
                    </Badge>
                  </td>
                  <td>{formatDate(invoice.dueDate)}</td>
                  <td>{invoice.paidAt ? formatDate(invoice.paidAt) : <Muted>-</Muted>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>
    </>
  );
};

export default BillingPage;
